// ============================================================
// usePlatformProviderStatus — health probes for the providers
// the platform depends on. Reads from /api/health/* when present.
// In demo mode (no live runtime) the hook reports "unknown" for
// every probe so the Infrastructure surface can show an honest
// not-yet-checked state instead of green/red theatre.
// ============================================================

import { useEffect, useState } from 'react'
import { getRuntime } from './_mode'

export type ProviderState = 'healthy' | 'degraded' | 'down' | 'unknown'

export interface ProviderProbe {
  id: string
  label: string
  state: ProviderState
  detail?: string
  checked_at?: string
}

export interface UsePlatformProviderStatusResult {
  probes: ProviderProbe[]
  loading: boolean
  error: string | null
}

const PROVIDERS: { id: string; label: string; path: string; authed?: boolean }[] = [
  { id: 'supabase', label: 'Supabase', path: '/api/health/supabase' },
  { id: 'redis',    label: 'Redis',    path: '/api/health/redis' },
  // Sentry probe is auth-required (api/health/sentry uses withAuth)
  // — fetch will return 401 when no session is present, which the
  // probe maps to the "unknown" state below instead of "down" so
  // signed-out operators don't see Sentry as broken.
  { id: 'sentry',   label: 'Sentry',   path: '/api/health/sentry', authed: true },
  { id: 'slack',    label: 'Slack',    path: '/api/health/slack',   authed: true },
]

export function usePlatformProviderStatus(): UsePlatformProviderStatusResult {
  const [probes, setProbes] = useState<ProviderProbe[]>(
    PROVIDERS.map((p) => ({
      id: p.id,
      label: p.label,
      state: 'unknown',
      detail: p.authed ? 'requires authenticated session' : undefined,
    })),
  )
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const { live } = getRuntime()

    async function load() {
      if (!live) {
        if (cancelled) return
        setLoading(false)
        return
      }

      try {
        // Pull the access token (if any) so authed probes can be
        // attempted from a signed-in session. The Sentry health
        // route is withAuth — without a bearer it will return 401
        // and we map that to 'unknown' (not 'down') so signed-out
        // operators don't see Sentry as broken.
        let bearer: string | null = null
        try {
          const stored =
            window.localStorage.getItem('square.auth.session') ||
            window.sessionStorage.getItem('square.auth.session')
          if (stored) {
            const parsed = JSON.parse(stored) as { access_token?: string }
            if (parsed && typeof parsed.access_token === 'string') {
              bearer = parsed.access_token
            }
          }
        } catch {
          bearer = null
        }

        const next = await Promise.all(
          PROVIDERS.map(async (p): Promise<ProviderProbe> => {
            try {
              const headers: Record<string, string> = {}
              if (p.authed && bearer) headers.Authorization = `Bearer ${bearer}`

              const res = await fetch(p.path, {
                credentials: 'omit',
                headers,
              })
              if (!res.ok) {
                if (p.authed && (res.status === 401 || res.status === 403)) {
                  return {
                    id: p.id,
                    label: p.label,
                    state: 'unknown',
                    detail: 'requires authenticated session',
                    checked_at: new Date().toISOString(),
                  }
                }
                return {
                  id: p.id,
                  label: p.label,
                  state: 'down',
                  detail: `HTTP ${res.status}`,
                  checked_at: new Date().toISOString(),
                }
              }
              const body = (await res.json().catch(() => ({}))) as {
                ok?: boolean
                state?: ProviderState
                detail?: string
                sentry?: {
                  ready?: boolean
                  ingestion?: { server_envelope?: { ok?: boolean }; browser_dsn?: { ok?: boolean } }
                }
              }
              // Sentry health returns { ok, sentry: { ready, ... } }
              // — derive a healthy/degraded state from the readiness
              // signals when present.
              if (p.id === 'sentry' && body.sentry) {
                const ready = body.sentry.ready === true
                const ingestionOk =
                  body.sentry.ingestion?.server_envelope?.ok === true ||
                  body.sentry.ingestion?.browser_dsn?.ok === true
                return {
                  id: p.id,
                  label: p.label,
                  state: ready ? 'healthy' : ingestionOk ? 'degraded' : 'down',
                  detail: ready
                    ? 'envelope + browser SDK ready'
                    : ingestionOk
                      ? 'partial readiness — see /api/health/sentry'
                      : 'no DSN resolved',
                  checked_at: new Date().toISOString(),
                }
              }
              const state: ProviderState =
                body.state || (body.ok ? 'healthy' : 'degraded')
              return {
                id: p.id,
                label: p.label,
                state,
                detail: body.detail,
                checked_at: new Date().toISOString(),
              }
            } catch (err) {
              return {
                id: p.id,
                label: p.label,
                state: 'down',
                detail: (err as Error).message,
                checked_at: new Date().toISOString(),
              }
            }
          }),
        )
        if (!cancelled) setProbes(next)
      } catch (err) {
        if (!cancelled) setError((err as Error).message)
      }
      if (!cancelled) setLoading(false)
    }

    load()
    return () => {
      cancelled = true
    }
  }, [])

  return { probes, loading, error }
}
